import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { SupabaseService } from './supabase.service';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

/// <summary>
/// HTTP interceptor that adds Supabase authentication headers to requests
/// </summary>
@Injectable()
export class SupabaseInterceptor implements HttpInterceptor {
  constructor(
    private supabaseService: SupabaseService,
    private router: Router,
    private messageService: MessageService
  ) {}

  /// <summary>
  /// Intercepts HTTP requests and adds Supabase authentication headers
  /// </summary>
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip authentication for public endpoints or non-API requests
    if (this.shouldSkipAuth(request)) {
      return next.handle(request);
    }

    return from(this.supabaseService.getAccessToken()).pipe(
      switchMap(token => {
        if (token) {
          // Clone the request and add the authorization header
          const authRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
          return next.handle(authRequest);
        } else {
          // No token available, proceed without auth header
          return next.handle(request);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle authentication errors
        if (error.status === 401 && request.url.includes('/api/v1/')) {
          this.handleUnauthorizedError();
        }
        return throwError(() => error);
      })
    );
  }

  /// <summary>
  /// Determines if authentication should be skipped for the request
  /// </summary>
  private shouldSkipAuth(request: HttpRequest<unknown>): boolean {
    // Skip auth for certain URLs (adjust as needed)
    const skipAuthUrls = [
      '/assets/',
      '/public/',
      '.json',
      '.css',
      '.js',
      '.ico'
    ];

    return skipAuthUrls.some(url => request.url.includes(url));
  }

  /// <summary>
  /// Handles 401 unauthorized errors
  /// </summary>
  private handleUnauthorizedError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Non autorisé',
      detail: 'Votre session a expiré. Veuillez vous reconnecter.'
    });

    // Sign out the user and redirect to login
    this.supabaseService.signOut().then(() => {
      this.router.navigate(['/login']);
    }).catch(error => {
      console.error('Error signing out:', error);
      this.router.navigate(['/login']);
    });
  }
}
